import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  booleanAttribute,
  input,
  PLATFORM_ID,
  signal,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import { TranslocoPipe } from '@jsverse/transloco';

import { filter, map, startWith } from 'rxjs/operators';

import { IconComponent } from '@shared/components';
import { EllipsisTooltipDirective } from '@shared/directives/tooltip';
import { FEATURE_FLAG_SERVICE } from '@shared/services/feature-flags';
import { SECTION_RIGHTS_SERVICE } from '@shared/services/rights';

import { isPathAllowed } from '../../core/authorization';
import { ENVIRONMENT } from '../../core/config/environment.token';
import { LanguageService } from '../services/language.service';
import { ThemeService } from '../services/theme.service';

import { NavDevItemComponent } from './components/nav-dev-item.component';
import { NavHeaderComponent } from './components/nav-header.component';
import { NavItemComponent } from './components/nav-item.component';
import { NavSectionComponent } from './components/nav-section.component';
import {
  DEFAULT_NAVIGATION,
  filterNavigationByRights,
  findExpandableSectionIds,
  isRootItem,
  type NavigationEntry,
  resolveDevNavigation,
  stripTenantSegment,
} from './models/navigation.model';
import { NavigationSidebarStore } from './navigation-sidebar.store';

const PEEK_ENTER_DELAY = 200;
const PEEK_LEAVE_DELAY = 300;

/**
 * Navigation sidebar component.
 * Provides hierarchical navigation with collapsible sections.
 * Auto-collapses on small screens and supports hover-to-peek when collapsed.
 */
@Component({
  selector: 'app-navigation-sidebar',
  standalone: true,
  templateUrl: './navigation-sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NavHeaderComponent, NavItemComponent, NavDevItemComponent, NavSectionComponent, IconComponent, TranslocoPipe, EllipsisTooltipDirective],
  host: {
    class: 'nav-sidebar relative z-40',
    '[attr.data-collapsed]': 'store.effectiveCollapsed()',
    '[attr.data-peeking]': 'store.peeking()',
    '[style.--nav-width]': 'store.width()',
    '(mouseleave)': 'onMouseLeave()',
    '(click)': 'onSidebarTap()',
    '(document:click)': 'onDocumentClick($event)',
  },
})
export class NavigationSidebarComponent {
  protected readonly store = inject(NavigationSidebarStore);
  protected readonly language = inject(LanguageService);
  protected readonly theme = inject(ThemeService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sectionRights = inject(SECTION_RIGHTS_SERVICE);
  private readonly featureFlags = inject(FEATURE_FLAG_SERVICE);
  private readonly env = inject(ENVIRONMENT);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /**
   * Whether the device can hover (has a fine pointer like a mouse).
   * Touch devices report `hover: none`; their synthetic mouse events must be
   * ignored, and peek is driven by tap instead. Defaults to true on SSR.
   */
  private readonly canHover = signal(true);

  /* ⚠️ DIVERGENCE LOCALE BackStage — voir ../PROVENANCE.md.
     `navigation` etait un signal interne fige sur DEFAULT_NAVIGATION, c'est-a-dire
     sur les sections du Manager. Devenu une ENTREE avec la meme valeur par defaut :
     sans liaison, le composant se comporte exactement comme la source. */
  readonly navigation = input<readonly NavigationEntry[]>(DEFAULT_NAVIGATION);

  /* DIVERGENCE LOCALE BackStage : logo de l'application, defaut inchange. */
  readonly logoSrc = input<string>('assets/Tick&LiveManager.png');

  /* DIVERGENCE LOCALE BackStage : les bascules de langue et de theme deviennent
     optionnelles. Defaut `true` — sans liaison, rendu identique a la source. */
  readonly showLanguageToggle = input(true, { transform: booleanAttribute });
  readonly showThemeToggle = input(true, { transform: booleanAttribute });

  /** Navigation filtered to the sections the current user is allowed to see. */
  protected readonly visibleNavigation = computed(() =>
    filterNavigationByRights(
      this.navigation(),
      (path) => isPathAllowed(path, this.sectionRights.getRights()),
      (flag) => this.featureFlags.isEnabled(flag)
    )
  );

  /** Current URL for active state detection, derived from the router via toSignal. */
  protected readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  /**
   * {@link currentUrl} with the leading `/{tenant}` segment stripped, so the
   * tenant-agnostic navigation config (absolute section paths) matches. Fed to
   * the pure `findExpandableSectionIds` (which knows nothing about tenancy).
   */
  protected readonly relativeUrl = computed(() => stripTenantSegment(this.currentUrl()));

  /**
   * Dev navigation entries (components gallery, staff feature-flag console),
   * pinned to the bottom.
   *
   * A `computed`, not a plain field: the environment flag is a build-time
   * constant but `FeatureFlagAdmin` is not — `hasRight()` reads
   * `AuthStore.user()` through the rights service, so this re-resolves when auth
   * settles (a field would freeze the pre-auth answer and permanently hide the
   * console).
   */
  protected readonly bottomNavigation = computed(() =>
    resolveDevNavigation({
      showGallery: this.env.showComponentsGallery,
      canAdminFeatureFlags: this.sectionRights.hasRight('FeatureFlagAdmin'),
    }),
  );

  /** Expose type guard to template */
  protected readonly isRootItem = isRootItem;

  /** Classes for the footer controls row — varies with collapsed state. */
  protected readonly footerControlsClasses = computed(() =>
    this.store.effectiveCollapsed()
      ? 'flex gap-1 flex-col'
      : 'flex gap-1 items-center justify-between'
  );

  private peekEnterTimer: ReturnType<typeof setTimeout> | null = null;
  private peekLeaveTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    if (isPlatformBrowser(inject(PLATFORM_ID))) {
      this.canHover.set(window.matchMedia('(hover: hover)').matches);
    }

    // Auto-expand sections whose children match the current route, and end any
    // transient peek on navigation (the touch equivalent of mouseleave on desktop).
    effect(() => {
      const url = this.relativeUrl();
      for (const id of findExpandableSectionIds(this.visibleNavigation(), url)) {
        this.store.expandSection(id);
      }
      untracked(() => {
        if (this.store.peeking()) this.store.setPeeking(false);
      });
    });

    this.destroyRef.onDestroy(() => {
      this.clearPeekTimers();
    });
  }

  /** Toggle sidebar collapsed state (persistence handled in the store). */
  protected toggleCollapsed(): void {
    this.store.setPeeking(false);
    this.clearPeekTimers();
    this.store.toggleCollapsed();
  }

  /**
   * Tap-to-peek (touch devices): a tap on the collapsed rail opens the transient
   * peek so section icons and labels become reachable. Relies on event bubbling —
   * a tap on a leaf link navigates first, then the navigation effect re-collapses.
   */
  protected onSidebarTap(): void {
    if (this.canHover()) return;
    if (this.store.effectiveCollapsed()) {
      this.store.setPeeking(true);
    }
  }

  /** Dismiss a touch peek when the user taps outside the sidebar. */
  protected onDocumentClick(event: MouseEvent): void {
    if (this.canHover() || !this.store.peeking()) return;
    const target = event.target;
    if (target instanceof Node && !this.host.nativeElement.contains(target)) {
      this.store.setPeeking(false);
    }
  }

  /** Hover-to-peek: start peek after delay */
  protected onMouseEnter(): void {
    if (!this.canHover()) return;
    if (!this.store.collapsed()) return;
    this.clearPeekLeaveTimer();
    this.peekEnterTimer = setTimeout(() => {
      if (this.store.collapsed()) {
        this.store.setPeeking(true);
      }
    }, PEEK_ENTER_DELAY);
  }

  /** Hover-to-peek: end peek after delay */
  protected onMouseLeave(): void {
    if (!this.canHover()) return;
    this.clearPeekEnterTimer();
    if (!this.store.peeking()) return;
    this.peekLeaveTimer = setTimeout(() => {
      this.store.setPeeking(false);
    }, PEEK_LEAVE_DELAY);
  }

  private clearPeekEnterTimer(): void {
    if (this.peekEnterTimer !== null) {
      clearTimeout(this.peekEnterTimer);
      this.peekEnterTimer = null;
    }
  }

  private clearPeekLeaveTimer(): void {
    if (this.peekLeaveTimer !== null) {
      clearTimeout(this.peekLeaveTimer);
      this.peekLeaveTimer = null;
    }
  }

  private clearPeekTimers(): void {
    this.clearPeekEnterTimer();
    this.clearPeekLeaveTimer();
  }
}
